import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, resource } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { AuthorizationStore, SOFTLINE_FEATURE_AUTHORIZATION } from '@softline/auth';
import { DefaultStore, Store, ValidationStore } from '@softline/core';
import { ResolvableModal, UiCoreModule } from '@softline/ui-core';
import moment from 'moment';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { TimestampCode } from '../../../data/timestamp-code.model';
import { Timestamp } from '../../../data/timestamp.model';
import { TimestampCodeService } from '../../../services/timestamp-code.service';
import {
  SOFTLINE_PERMISSION_TIMESTAMP_CUSTOM,
  SOFTLINE_PERMISSION_TIMESTAMP_SELECT_EMPLOYEE,
} from '../../../stempelerfassung.permissions';
import {
  SOFTLINE_FEATURE_TIMESTAMP,
  SOFTLINE_FEATURE_TIMESTAMP_CODE,
  SOFTLINE_FEATURE_TIMESTAMP_EMPLOYEE,
  TimestampCodeStore,
  TimestampEmployeeStore,
  TimestampStore,
} from '../../../stempelerfassung.shared';

@Component({
  selector: 'soft-record-timestamp-dialog',
  templateUrl: './record-timestamp-dialog.component.html',
  styleUrls: ['./record-timestamp-dialog.component.scss'],
  imports: [CommonModule, UiCoreModule, ReactiveFormsModule],
  providers: [TimestampCodeService],
})
export class RecordTimestampDialogComponent
  extends ResolvableModal<Timestamp>
  implements OnInit, OnDestroy
{
  private validationSubscription?: Subscription;

  readonly #service = inject(TimestampCodeService);

  form: UntypedFormGroup = new UntypedFormGroup(
    {
      employee: new UntypedFormControl(undefined),
      flag: new UntypedFormControl('arrival'),
      code: new UntypedFormControl(undefined),
      timestamp: new UntypedFormControl(moment().toISOString()),
    },
    { asyncValidators: (control) => this.validateForm(this.store, control) }
  );

  codes$: Observable<readonly TimestampCode[]> = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP_CODE,
    TimestampCodeStore.getters.all
  );

  employees$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP_EMPLOYEE,
    TimestampEmployeeStore.getters.all
  );

  validation$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP,
    ValidationStore.getters.validation.validation
  );

  templateLoading$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP,
    TimestampStore.getters.loading
  );

  canModifyTime$ = this.store.observe(
    SOFTLINE_FEATURE_AUTHORIZATION,
    AuthorizationStore.getters.authorized,
    SOFTLINE_PERMISSION_TIMESTAMP_CUSTOM
  );
  canModifyEmployee$ = this.store.observe(
    SOFTLINE_FEATURE_AUTHORIZATION,
    AuthorizationStore.getters.authorized,
    SOFTLINE_PERMISSION_TIMESTAMP_SELECT_EMPLOYEE
  );

  readonly flag = toSignal(
    this.form.get('flag')!.valueChanges.pipe(distinctUntilChanged()),
    {
      initialValue: undefined,
    }
  );

  readonly codes = resource({
    request: () => this.flag(),
    loader: async (params) => {
      if (!params?.request) return Promise.resolve([]);
      return await firstValueFrom(this.#service.retrieveCodes(params.request));
    },
  });

  constructor(private store: Store) {
    super();
  }

  async ngOnInit(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_TIMESTAMP,
      DefaultStore.actions.default.load,
      {}
    );

    this.store.observe(SOFTLINE_FEATURE_TIMESTAMP, DefaultStore.getters.default.default);

    this.validationSubscription = this.form.valueChanges
      .pipe(debounceTime(100))
      .subscribe(async (entity) => {
        await this.store.dispatch(
          SOFTLINE_FEATURE_TIMESTAMP,
          ValidationStore.actions.validation.validate,
          { entity }
        );
      });

    this.store
      .observe(SOFTLINE_FEATURE_TIMESTAMP, DefaultStore.getters.default.default)
      .pipe(
        filter((o) => !!o),
        take(1)
      )
      .subscribe((o) => this.form.patchValue(o));
  }

  ngOnDestroy(): void {
    if (this.validationSubscription && !this.validationSubscription.closed) {
      this.validationSubscription.unsubscribe();
    }
    this.validationSubscription = undefined;
  }

  onSubmit(timestamp: Timestamp): void {
    this.resolve(timestamp);
  }

  private validateForm(store: Store, control: AbstractControl): Observable<any> {
    return store
      .observe(SOFTLINE_FEATURE_TIMESTAMP, ValidationStore.getters.validation.valid)
      .pipe(map((o) => ({ isValid: o })));
  }

  filterCodes(code: TimestampCode, flag: 'arrival' | 'departure' | undefined): boolean {
    return code.flag === flag;
  }

  onFlagChange() {
    this.form.patchValue({ code: null });
  }
}
